import { DragHandle } from '@creditornot/cb-icons';
import { cssThemedValue } from '@creditornot/cb-ingredients';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import React, { useMemo } from 'react';
import styled, { css } from 'styled-components';

import { Crop, CropPreview } from '../ImageCropper';
import ImageWrapper from '../ImageWrapper';

type Props = {
  crop: Crop;
  draggingDisabled: boolean;
  imageId: string;
  imageUrl: string;
  rightContent: React.ReactNode;
};

const Root = styled.div<{ dragging: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-block-end: 12px;
  position: relative;

  ${({ dragging }) =>
    dragging &&
    css`
      z-index: 1;
    `}
`;

const Left = styled.div`
  display: flex;
  align-items: center;
`;

const StyledDragHandle = styled(DragHandle)<{ disabled: boolean }>`
  cursor: grab;
  margin-inline-end: 16px;
  fill: ${cssThemedValue('textPrimary')};

  ${({ disabled }) =>
    disabled &&
    css`
      cursor: not-allowed;
      opacity: 0.6;
    `}
`;

const StyledImageWrapper = styled(ImageWrapper)`
  width: 90px;
`;

const DraggableImage = ({ crop, imageId, imageUrl, draggingDisabled, rightContent }: Props) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: imageId,
    disabled: draggingDisabled,
  });

  const style = useMemo(
    () => ({
      transform: CSS.Transform.toString(transform),
      transition,
    }),
    [transform, transition],
  );

  return (
    <Root ref={setNodeRef} dragging={isDragging} style={style}>
      <Left>
        <StyledDragHandle disabled={draggingDisabled} width={16} {...attributes} {...listeners} />
        <StyledImageWrapper>
          <CropPreview crop={crop} imageUrl={imageUrl} />
        </StyledImageWrapper>
      </Left>
      {rightContent}
    </Root>
  );
};

export default DraggableImage;
